import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const CustomLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const login = useLogin();
    const notify = useNotify();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Clear previous errors
        setError('');
        setLoading(true);
        
        try {
            await login({ username, password });
            // Success - React-Admin will handle the redirect
        } catch (error) {
            // Display error message
            const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = () => {
        // Clear error when user starts typing
        if (error) {
            setError('');
        }
    };

    const handleSignUp = () => {
        // Navigate to the sign up page
        
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f5f5f5',
            }}
        >
            <Card sx={{ minWidth: 400, p: 3 }}>
                <CardContent>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            NeverKnock Admin
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            Sign in to access the admin panel
                        </Typography>
                    </Box>
                    
                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="Email"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                handleInputChange();
                            }}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            disabled={loading}
                            error={!!error}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                handleInputChange();
                            }}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            required
                            disabled={loading}
                            error={!!error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            type="submit"
                            fullWidth
                            disabled={loading}
                            sx={{ mt: 3, mb: 2, minHeight: 48 }}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    <Button
                            variant="contained"
                            onClick={handleSignUp}
                            fullWidth   
                            disabled={loading}
                            sx={{ mt: 3, mb: 2, minHeight: 48 }}
                        >
                            Sign Up
                        </Button>
                    
                    {/* Help Text */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="textSecondary" align="center">
                            Need help? Contact{' '}
                            <a 
                                href="mailto:support@neverknock.com" 
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                                support@neverknock.com
                            </a>
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CustomLogin; 